// ----------------------------------------------------------
// © Copyright 2021 European Space Agency, 2021
// This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this [source code/executable] package. No part of the package, including this file, may be copied, modified, propagated, or distributed except according to the terms contained in the file ‘LICENSE.txt’.
// -----------------------------------------------------------
import dayjs from "dayjs";
/**
 * Get the loglevel from the search params
 */
const getParameterByName = (name, url = window.location.href) => {
    const searchParam = new URLSearchParams(new URL(url).search);
    const namedParam = searchParam.get(name);
    if (namedParam) {
        return namedParam;
    }
    return null;
};
//
const loggerConfig = {
    global: {
        logLevel: getParameterByName('loglevel') || 'error'
    },
    api: {
        logLevel: getParameterByName('api.loglevel') || getParameterByName('loglevel') || 'info'
    },
    service: {
        logLevel: getParameterByName('service.loglevel') || getParameterByName('loglevel') || 'info'
    },
    component: {
        logLevel: getParameterByName('component.loglevel') || getParameterByName('loglevel') || 'info'
    },
    ui: {
        logLevel: getParameterByName('ui.loglevel') || getParameterByName('loglevel') || 'info'
    },
};
const logType = ['error', 'warn', 'info', 'debug'];
const colors = {
    'error': '#f1666a',
    'warn': '#ffcc4e',
    'info': ' #00619E',
    'debug': '#00AE9D',
};
/**
 * logging interface, use it to abstract where we are logging in.
 * It will also format the logging
 */
export class Logger {
    constructor(scope, name) {
        this.handlers = [];
        // for testing purpose we put the config on the object so that we can alterate it within the test
        this.config = loggerConfig;
        this.scope = scope;
        this.name = name;
        // by default we push the console in the list of handlers, could be good to add external providers
        this.handlers.push(console);
    }
    /**
     * Check that we have the good level of log setup
     * @param type type of log that should be done
     * @returns
     */
    _shouldLog(type) {
        const logLevel = this.config[this.scope] ? this.config[this.scope].logLevel : this.config.global.logLevel;
        return logType.indexOf(type) <= logType.indexOf(logLevel);
    }
    /**
     * Do the log if needed, send the message formated to all the handlers
     * @param type Type of log
     * @param message message to log
     */
    _doLog(type, message, arg) {
        if (this._shouldLog(type)) {
            const formattedType = [`%c${type.toUpperCase()}`, `color: white;background:${colors[type]};font-weight: bold; font-size:10px; padding:2px 6px; border-radius: 2px`];
            const formattedMessage = `${dayjs().format('YYYY-MM-DD-hh-mm-ss-sss')} (${this.scope}/${this.name}) ${this._sanitizeMessage(message)}`;
            this.handlers.forEach((handler) => {
                if (arg)
                    handler[type](...formattedType, formattedMessage, arg);
                else
                    handler[type](...formattedType, formattedMessage);
            });
        }
    }
    /**
     * Sanitize the message, in case we have something else than a string we apply stringification
     * @param message
     * @returns
     */
    _sanitizeMessage(message) {
        return typeof message != 'string' ? JSON.stringify(message) : message;
    }
    debug(message, arg = null) {
        this._doLog('debug', message, arg);
    }
    info(message, arg = null) {
        this._doLog('info', message, arg);
    }
    warn(message, arg = null) {
        this._doLog('warn', message, arg);
    }
    error(message, arg = null) {
        this._doLog('error', message, arg);
    }
}
